/* Sorana / Minion: storefront quantity UX. Not a server-side checkout rule. */
(() => {
  'use strict';
  const rulesCache = new WeakMap();
  let pendingCart = 0;
  let revision = 0;
  let checkoutBusy = false;
  const allowedSubmissions = new WeakSet();
  const cartSelector = 'input[data-sorana-cart-quantity]';
  const root = () => window.Shopify?.routes?.root || '/';

  function ruleFor(input) {
    const step = Math.max(1, Number(input.dataset.step) || 1);
    const minimum = Math.ceil(Math.max(1, Number(input.dataset.minimum) || 1) / step) * step;
    const suppliedMax = Number(input.dataset.maximum ?? -1);
    return { minimum, step, maximum: suppliedMax < 0 ? -1 : Math.floor(suppliedMax / step) * step };
  }
  const restricted = rule => rule.minimum > 1 || rule.step > 1;
  const valid = (quantity, rule) => Number.isInteger(quantity) && quantity >= rule.minimum && quantity % rule.step === 0 && (rule.maximum < 0 || quantity <= rule.maximum);
  function rounded(quantity, rule) {
    if (rule.maximum >= 0 && rule.maximum < rule.minimum) return null;
    let value = Math.max(rule.minimum, Math.ceil((Number(quantity) || rule.minimum) / rule.step) * rule.step);
    if (rule.maximum >= 0) value = Math.min(value, rule.maximum);
    return value;
  }
  function message(anchor, text, error = false) {
    if (!anchor) return;
    let node = Array.from(anchor.children).find(child => child.classList.contains('sorana-quantity-message'));
    if (!node && !text) return;
    if (!node) {
      node = document.createElement('p');
      node.className = 'sorana-quantity-message';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      anchor.appendChild(node);
    }
    if (node.textContent !== text) node.textContent = text;
    node.dataset.error = String(error);
    node.hidden = !text;
  }
  function inputMessage(input, text, error = false) {
    const control = input.closest('quantity-input');
    if (!control) { message(input.form || input.parentElement, text, error); return; }
    const row = control.parentElement;
    let node = row.nextElementSibling;
    if (!node?.hasAttribute('data-sorana-input-message')) {
      if (!text) return;
      node = document.createElement('p');
      node.className = 'sorana-quantity-message';
      node.dataset.soranaInputMessage = '';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      row.insertAdjacentElement('afterend', node);
    }
    if (node.textContent !== text) node.textContent = text;
    node.dataset.error = String(error);
    node.hidden = !text;
  }
  function updateControls(input) {
    const rule = ruleFor(input);
    input.min = String(rule.minimum);
    input.step = String(rule.step);
    const impossible = rule.maximum >= 0 && rule.maximum < rule.minimum;
    if (rule.maximum >= rule.minimum) input.max = String(rule.maximum);
    else input.removeAttribute('max');
    const control = input.closest('quantity-input');
    const minus = control?.querySelector('button[name="minus"]');
    const plus = control?.querySelector('button[name="plus"]');
    if (minus) minus.disabled = impossible || Number(input.value) <= rule.minimum;
    if (plus) plus.disabled = impossible || (rule.maximum >= 0 && Number(input.value) >= rule.maximum);
  }
  function normaliseInput(input) {
    const rule = ruleFor(input);
    const value = rounded(input.value, rule);
    if (value === null) {
      input.setAttribute('aria-invalid', 'true');
      inputMessage(input, 'Stoc insuficient pentru cantitatea minimă. Elimină produsul din coș sau alege altă variantă.', true);
      updateControls(input);
      return false;
    }
    const previous = input.value;
    input.value = String(value);
    input.removeAttribute('aria-invalid');
    const hint = restricted(rule) ? `Minim ${rule.minimum} bucăți; multiplu de ${rule.step}.` : '';
    inputMessage(input, Number(previous) !== value ? `Cantitatea a fost ajustată la ${value} bucăți. ${hint}` : hint);
    updateControls(input);
    return true;
  }
  function formRules(form) {
    const script = form.querySelector('[data-sorana-quantity-rules]');
    if (!script) return null;
    if (!rulesCache.has(script)) {
      try { rulesCache.set(script, JSON.parse(script.textContent)); }
      catch (_) { return null; }
    }
    return rulesCache.get(script);
  }
  function syncForm(form) {
    const rules = formRules(form);
    if (!rules) return;
    const id = form.querySelector('[name="id"]')?.value;
    const rule = rules[id] || Object.values(rules)[0];
    if (!rule) return;
    let input = form.querySelector('[name="quantity"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = String(rule.minimum);
      form.appendChild(input);
    }
    const changed = input.dataset.soranaVariant !== id;
    input.dataset.soranaQuantity = '';
    input.dataset.minimum = rule.minimum;
    input.dataset.step = rule.step;
    input.dataset.maximum = rule.maximum;
    input.dataset.soranaVariant = id || '';
    if (changed || !input.dataset.soranaInitialized) normaliseInput(input);
    input.dataset.soranaInitialized = 'true';
    updateControls(input);
    const dynamic = form.querySelector('[data-sorana-dynamic-checkout]');
    if (dynamic) dynamic.hidden = Object.values(rules).some(restricted);
  }
  async function getJSON(url) {
    const response = await timedFetch(url);
    return response.json();
  }
  async function timedFetch(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart request failed');
      return response;
    } finally { clearTimeout(timer); }
  }
  async function prepareAdd(form) {
    syncForm(form);
    const input = form.querySelector('[name="quantity"]');
    if (!input?.hasAttribute('data-sorana-quantity')) return '';
    if ((form.getAttribute('id') || '').startsWith('sticky-product-form-')) {
     const mainForm = document.getElementById((form.getAttribute('id') || '').replace('sticky-product-form-', 'product-form-'));
      if (mainForm?.querySelector('[name="id"]')?.value === form.querySelector('[name="id"]')?.value) {
        const mainQuantity = mainForm.querySelector('[name="quantity"]');
        if (mainQuantity) input.value = mainQuantity.value;
      }
    }
    const fail = text => { inputMessage(input, text, true); return text; };
    if (!normaliseInput(input)) return 'Stoc insuficient pentru cantitatea minimă.';
    const rule = ruleFor(input);
    if (!restricted(rule)) return '';
    if (pendingCart) return fail('Coșul se actualizează. Încearcă din nou în câteva secunde.');
    const id = form.querySelector('[name="id"]')?.value;
    const quantity = Number(input.value);
    const before = revision;
    try {
      const cart = await getJSON(`${root()}cart.js`);
      if (id !== form.querySelector('[name="id"]')?.value || quantity !== Number(input.value) || before !== revision || pendingCart) {
        return fail('Selecția sau coșul s-a schimbat. Apasă din nou „Adaugă în coș”.');
      }
      const existing = cart.items.filter(item => String(item.variant_id) === id);
      if (existing.some(item => !valid(item.quantity, { ...rule, maximum: -1 }))) {
        return fail('Acest produs are deja o cantitate nevalidă în coș. Corecteaz-o în coș înainte de a adăuga alte bucăți.');
      }
      const inCart = existing.reduce((total, item) => total + item.quantity, 0);
      if (rule.maximum >= 0 && inCart + quantity > rule.maximum) {
        const remaining = Math.max(0, Math.floor((rule.maximum - inCart) / rule.step) * rule.step);
        return fail(`Stoc insuficient. Ai deja ${inCart} bucăți în coș; mai poți adăuga ${remaining} bucăți.`);
      }
      return '';
    } catch (_) {
      return fail('Nu am putut verifica stocul și coșul. Reîncearcă.');
    }
  }
  function syncCart() {
    const inputs = Array.from(document.querySelectorAll(cartSelector));
    inputs.forEach(input => {
      updateControls(input);
      const rule = ruleFor(input);
      const committed = Number(input.dataset.cartQuantity);
      if (!valid(committed, rule)) {
        inputMessage(input, `Alege minimum ${rule.minimum} bucăți, în multipli de ${rule.step}, în limita stocului.`, true);
      } else if (!input.closest('cart-items, cart-drawer-items')?.classList.contains('cart__items--disabled')) {
        inputMessage(input, restricted(rule) ? `Minim ${rule.minimum} bucăți; multiplu de ${rule.step}.` : '');
      }
    });
    const hasRules = inputs.some(input => restricted(ruleFor(input)));
    document.querySelectorAll('[data-sorana-cart-accelerated]').forEach(node => { node.hidden = hasRules; });
  }
  function checkoutMessage(button, text) {
    const anchor = button?.parentElement || document.querySelector('cart-items, cart-drawer') || document.body;
    message(anchor, text, true);
    const node = Array.from(anchor.children).find(child => child.classList.contains('sorana-quantity-message'));
    if (node) node.dataset.soranaCheckoutMessage = '';
  }
  async function guardCheckout(event, button, form) {
    if (form && allowedSubmissions.has(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    let dirty = false;
    document.querySelectorAll(cartSelector).forEach(input => {
      if (input.value !== input.dataset.cartQuantity) {
        dirty = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    if (dirty || pendingCart) {
      checkoutMessage(button, 'Așteaptă actualizarea cantităților, apoi apasă din nou „Finalizare”.');
      return;
    }
    checkoutBusy = true;
    if (button) button.setAttribute('aria-busy', 'true');
    const before = revision;
    try {
      const response = await timedFetch(`${root()}cart?section_id=sorana-quantity-cart`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const snapshot = html.querySelector('[data-sorana-cart-snapshot]');
      if (!snapshot) throw new Error('Missing cart snapshot');
      const lines = Array.from(snapshot.querySelectorAll('[data-sorana-validation-line]'));
      if (!lines.length) { checkoutMessage(button, 'Coșul este gol.'); return; }
      const invalid = lines.find(line => !valid(Number(line.dataset.quantity), ruleFor(line)));
      if (invalid) {
        const rule = ruleFor(invalid);
        checkoutMessage(button, `${invalid.dataset.title}: alege minimum ${rule.minimum} bucăți, în multipli de ${rule.step}, în limita stocului. Actualizează cantitatea în coș.`);
        return;
      }
      const unsaved = Array.from(document.querySelectorAll(cartSelector)).some(input => input.value !== input.dataset.cartQuantity);
      if (pendingCart || revision !== before || unsaved) {
        checkoutMessage(button, 'Coșul s-a schimbat. Așteaptă actualizarea și apasă din nou „Finalizare”.');
        return;
      }
      if (form && button?.form === form && button.type === 'submit') {
        allowedSubmissions.add(form);
        try { form.requestSubmit(button); } finally { allowedSubmissions.delete(form); }
      } else {
        window.location.assign(`${root()}checkout`);
      }
    } catch (_) {
      checkoutMessage(button, 'Nu am putut verifica cantitățile. Reîncearcă sau reîncarcă pagina.');
    } finally {
      checkoutBusy = false;
      button?.removeAttribute('aria-busy');
    }
  }
  document.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.name === 'id' && input.form) syncForm(input.form);
    if (input.hasAttribute('data-sorana-quantity')) normaliseInput(input);
    if (input.matches(cartSelector)) revision += 1;
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest('button[name="checkout"], input[name="checkout"][type="submit"], a[href="/checkout"]');
    if (button && !button.disabled) guardCheckout(event, button, button.form);
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    const button = event.submitter;
    if (button?.name === 'checkout' || form.id === 'cart-notification-form') guardCheckout(event, button || form.querySelector('[type="submit"]'), form);
  }, true);
  function refresh() {
    document.querySelectorAll('form').forEach(form => {
      if (form.querySelector('[data-sorana-quantity-rules]')) syncForm(form);
    });
    syncCart();
  }
  let refreshQueued = false;
  const observer = new MutationObserver(records => {
    if (!records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && !node.classList?.contains('sorana-quantity-message')))) return;
    if (refreshQueued) return;
    refreshQueued = true;
    queueMicrotask(() => { refreshQueued = false; refresh(); });
  });
  window.SoranaQuantity = {
    syncForm, syncCart, normaliseInput, prepareAdd,
    cartRequestStarted() { pendingCart += 1; revision += 1; },
    cartRequestFinished() { pendingCart = Math.max(0, pendingCart - 1); revision += 1; syncCart(); }
  };
  const start = () => { refresh(); observer.observe(document.body, { childList: true, subtree: true }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
